import * as React from 'react'

import {
  applyContextMenuHighlight,
  clearContextMenuHighlight,
  listEnabledContextMenuItems,
  nextContextMenuIndex,
} from '@/lib/editor/context-menu-keyboard'

import { resolveContextMenuKeyAction } from '../helpers/context-menu-key-action'

/**
 * Keyboard highlight for the open menu. The active index resets whenever the menu opens or moves,
 * is clamped to the enabled rows after every render, and is driven by a capture-phase window
 * keydown listener (arrows, Home/End, Enter/Space, Escape) that stops the keys it handles.
 */
export function useContextMenuActiveIndex({
  open,
  position,
  onOpenChange,
  menuRef,
  children,
}: {
  open: boolean
  position: { x: number; y: number }
  onOpenChange: (open: boolean) => void
  menuRef: React.RefObject<HTMLDivElement | null>
  children: React.ReactNode
}) {
  const activeIndexRef = React.useRef(-1)
  const [activeIndex, setActiveIndex] = React.useState(-1)

  React.useEffect(() => {
    if (!open) {
      activeIndexRef.current = -1
      setActiveIndex(-1)
      return
    }

    activeIndexRef.current = -1
    setActiveIndex(-1)
  }, [open, position.x, position.y])

  React.useLayoutEffect(() => {
    const menu = menuRef.current

    if (!open || !menu) return

    if (activeIndex < 0) {
      clearContextMenuHighlight(menu)
      return
    }

    const items = listEnabledContextMenuItems(menu)
    const clamped = items.length === 0 ? -1 : Math.min(activeIndex, items.length - 1)

    if (clamped !== activeIndex) {
      activeIndexRef.current = clamped
      setActiveIndex(clamped)
      return
    }

    applyContextMenuHighlight(menu, clamped)
  }, [open, activeIndex, children, menuRef])

  React.useEffect(() => {
    if (!open) return

    const onKeyDown = (e: KeyboardEvent) => {
      const menu = menuRef.current

      if (!menu) return

      const items = listEnabledContextMenuItems(menu)
      const count = items.length

      if (count === 0) return

      const action = resolveContextMenuKeyAction(e.key, count)

      if (!action) return

      const idx = activeIndexRef.current

      // Enter/Space with nothing highlighted is left alone (not prevented).
      if (action.kind === 'activate' && (idx < 0 || idx >= count)) return

      e.preventDefault()
      e.stopPropagation()

      switch (action.kind) {
        case 'step': {
          setActiveIndex((prev) => {
            const next = nextContextMenuIndex(prev, action.delta, count)

            activeIndexRef.current = next

            return next
          })
          break
        }

        case 'jump': {
          activeIndexRef.current = action.index
          setActiveIndex(action.index)
          break
        }

        case 'activate': {
          items[idx].click()
          break
        }

        case 'close': {
          onOpenChange(false)
          break
        }
      }
    }

    window.addEventListener('keydown', onKeyDown, true)

    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open, onOpenChange, menuRef])
}
